<template>
  <div class="row">
    <div :class="colClass">
      <form autocomplete="off" @submit.prevent="saveClicked">
        <template
          v-for="(unused_SlotContent, slotName) in $slots"
          :key="slotName"
        >
          <slot
            v-if="slotName !== 'cancel-button'"
            :name="slotName"
            :v-form="$v.form"
            :field-errors="fieldErrors"
            :validation-messages="validationMessages"
            :get-is-invalid-class="getIsInvalidClass"
            :get-each-item-field-v-field="getEachItemFieldVField"
          />
        </template>
        <!-- Invalid Fields Display -->
        <InvalidFields
          v-if="formSubmitted"
          :invalid-fields-labels="invalidFieldsLabels"
        />

        <div
          class="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-4"
        >
          <div class="d-flex justify-content-start align-items-center">
            <small> <span class="mandatory">*</span> Required </small>
          </div>
          <!-- Save and Cancel Buttons -->
          <div class="d-flex flex-wrap justify-content-end gap-2 ms-auto">
            <slot name="cancel-button" :is-form-dirty="isFormDirty" />

            <!-- Optional Reset Button -->
            <StoreButton
              v-if="resetButtonText"
              type="reset"
              :disabled="!isFormDirty"
              @click="resetClicked"
            >
              <span>{{ resetButtonText }}</span>
            </StoreButton>

            <!-- Save Button -->
            <StoreButton
              type="submit"
              :variant="submitBtnVariant"
              :disabled="!isFormDirty || saveBtnDisabled"
            >
              <span>{{ saveButtonText }}</span>
            </StoreButton>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
<script lang="ts">
// State to track the last user decision for a specific navigation
</script>
<script setup lang="ts" generic="T extends Object">
import InvalidFields from "~/components/shared/forms/InvalidFields.vue";
import { useVuelidate } from "@vuelidate/core";
import { useFieldErrorMixin } from "~/mixins/useFieldErrorsMixin";
import { useInvalidFormFieldsMixin } from "~/mixins/useInvalidFormFieldsMixin";
import { ref, onBeforeUnmount } from "vue";
import { useUiStore } from "~/stores/ui/uistore";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import type { ButtonVariant } from "~/components/shared/buttons/StoreButton.vue";

/* eslint-disable @typescript-eslint/no-explicit-any */
type FieldValues = Record<string, any>; // Generic for any key-value pair in your form

// Define props
const props = defineProps({
  saveButtonText: { type: String, required: false, default: "Save" },
  saveBtnDisabled: { type: Boolean, required: false, default: false },
  savedCallback: { type: Function, required: true },
  resetButtonText: { type: String, default: "" },

  validationMessages: { type: Object, required: true },
  fieldLabelsMap: { type: Object, required: true },
  validationRulesFunction: { type: Function, required: true },

  modelValue: {
    type: Object as PropType<T extends FieldValues ? T : never>,
    default: null,
  },
  initialState: {
    type: Object as PropType<T extends FieldValues ? T : never>,
    default: null,
  },
  colClass: { type: String, default: "col-lg-8 col-md-10 col-sm-12" },
  preventDirtyNavigation: { type: Boolean, default: true },
  submitBtnVariant: {
    type: String as PropType<ButtonVariant>,
    default: "primary",
  },
});

const rules = computed(() => ({
  form: props.validationRulesFunction(props.modelValue),
}));
const $v = useVuelidate(rules, { form: props.modelValue });
const {
  fieldErrors,
  withFieldErrors,
  getIsInvalidClass,
  getEachItemFieldVField: _getEachItemFieldVField,
} = useFieldErrorMixin();
const { invalidFieldsLabels, getInvalidFormFields } =
  useInvalidFormFieldsMixin(fieldErrors);

const isFormDirty = computed(() => {
  // compare initial State and modelValue
  return (
    JSON.stringify(props.modelValue) !== JSON.stringify(props.initialState)
  );
});

const emit = defineEmits(["isFormDirty"]);

watch(
  isFormDirty,
  (newValue) => {
    emit("isFormDirty", newValue);
    if (props.preventDirtyNavigation) {
      useUiStore().isFormDirty = newValue;
    } else {
      useUiStore().isFormDirty = false;
    }
  },
  { immediate: true },
);

const formSubmitted = ref(false);
// Wrap getEachItemFieldVField to gate on formSubmitted
const getEachItemFieldVField = (
  vFormItems: Parameters<typeof _getEachItemFieldVField>[0],
  index: Parameters<typeof _getEachItemFieldVField>[1],
  fieldName: Parameters<typeof _getEachItemFieldVField>[2],
) => {
  return _getEachItemFieldVField(
    vFormItems,
    index,
    fieldName,
    formSubmitted.value,
  );
};

// Save button click handler
const saveClicked = async () => {
  formSubmitted.value = true;
  const isValid = await $v.value.form.$validate();
  if (isValid) {
    try {
      const saveSuccessful = await withFieldErrors(props.savedCallback());
      if (saveSuccessful) {
        return;
      }
    } catch (err) {
      console.error("callback failed", err);
    }
  }
  getInvalidFormFields($v.value.form, props.fieldLabelsMap);
};

// Reset button click handler
const resetClicked = () => {
  // Deep copy initialState into modelValue
  if (props.modelValue && props.initialState) {
    /* eslint-disable vue/no-mutating-props */
    Object.assign(props.modelValue, deepClone(props.initialState));
  }
  $v.value.form.$reset(); // Reset Vuelidate state
  fieldErrors.value = [];
  formSubmitted.value = false;
  invalidFieldsLabels.value = [];
};

// prevent navigation if form dirty for external pages
const listenerAdded = ref(false);
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (isFormDirty.value && props.preventDirtyNavigation) {
    e.preventDefault();
    e.returnValue = ""; // required for Chrome
  }
};
onMounted(() => {
  if (!listenerAdded.value) {
    window.addEventListener("beforeunload", handleBeforeUnload);
    listenerAdded.value = true; // mark listener as added
  }
});

defineExpose({
  resetForm: resetClicked,
});

// Remove listener when this component unmounts
onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", handleBeforeUnload);
  listenerAdded.value = false; // reset flag if needed
});

// deep clone function preserves undefined values which Json.stringify does not
function deepClone<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(deepClone) as T;
  }

  if (value && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>)) {
      result[key] = deepClone((value as Record<string, unknown>)[key]);
    }
    return result as T;
  }

  return value;
}
</script>
